import React from 'react';
import { Trans } from '@lingui/macro';

import { Button, Display, Flex } from '@rover/kibble/core';
import Section from '@rover/kibble/patterns/Section';
import useDeviceContext from '@rover/react-lib/src/context/useDeviceContext';
import getSearchUrl from '@rover/react-lib/src/utils/getSearchUrl';
import { CountryConfiguration, OptimizedSearchResponse } from '@rover/rsdk/src/apiClient/latest';
import { emitAnalyticsEvent } from '@rover/rsdk/src/modules/Analytics';
import { PriceTransparencyLevel, SearchFilters } from '@rover/types';
import { PetType } from '@rover/types/src/Pet';
import { PriceTransparencyLevels } from '@rover/types/src/PriceTransparencyLevel';
import {
  SERVICE_TYPE_FOR_SERVICE_LANDING_ENUM,
  SERVICE_TYPE_SLUG_FOR_SERVICE_LANDING_ENUM,
  ServiceLandingEnum,
} from '@rover/types/src/ServiceLanding';
import { storeCrossPageAnalyticsEvent } from '@rover/utilities/fireCrossPageEvent';

import SearchResultsLayout from '../../components/SearchResultsLayout';
import { getSearchMapData, getSelectedSitterSearchUrl } from '../../utilities';
import {
  ClickSitterCard,
  SearchResultLayoutType,
  SearchResultsCtaClick,
  SitterCardType,
} from '../../utilities/analyticsEvents';
import { getHardcodedContentForServiceTypeSlug } from '../constants';

type Props = {
  searchResults: OptimizedSearchResponse;
  language: string;
  countryConfig: Pick<CountryConfiguration, 'urls' | 'bookingFeePercentage' | 'bookingFeeCap'>;
  searchErrorMessage?: string;
  mapSettings: React.ComponentProps<typeof SearchResultsLayout>['mapSettings'];
  serviceLandingType: ServiceLandingEnum;
  adwordslocation?: string;
};
export const CTA_MAX_WIDTH = '375px';

const getPetTypeFiltersFromServiceLandingType = (
  serviceLandingType: ServiceLandingEnum
): Partial<Pick<SearchFilters, 'petType' | 'puppy'>> => {
  if (
    serviceLandingType === ServiceLandingEnum.CAT_BOARDING ||
    serviceLandingType === ServiceLandingEnum.CAT_SITTING
  )
    return { petType: [PetType.CAT] };

  if (serviceLandingType === ServiceLandingEnum.PUPPY_BOARDING) return { puppy: true };

  return {};
};

const ServiceLandingResultsLayout: React.FC<Props> = ({
  searchResults,
  language,
  countryConfig,
  searchErrorMessage = '',
  mapSettings,
  serviceLandingType,
  adwordslocation,
}) => {
  const { isDesktop } = useDeviceContext();
  const searchFilters: Partial<SearchFilters> = {
    serviceType: SERVICE_TYPE_FOR_SERVICE_LANDING_ENUM[serviceLandingType],
    adwordslocation,
    ...getPetTypeFiltersFromServiceLandingType(serviceLandingType),
  };
  const serviceTypeSlug = SERVICE_TYPE_SLUG_FOR_SERVICE_LANDING_ENUM[serviceLandingType];
  const HARDCODED_CONTENT = getHardcodedContentForServiceTypeSlug(serviceTypeSlug);
  const searchResultsWithSelectedSitterUrl = (searchResults.results ?? []).map((searchResult) => ({
    ...searchResult,
    webUrl: getSelectedSitterSearchUrl(searchFilters, language, searchResult),
  }));

  return (
    <Section mt={['10x']} px={['0x', '4x']}>
      <Flex
        justifyContent="center"
        alignItems="center"
        paddingTop="1x"
        flexDirection="column"
        position="relative"
      >
        <Display
          size="500"
          textAlign="center"
          responsive={false}
          px={['4x', '0x']}
          mb={['6x', '8x']}
        >
          {HARDCODED_CONTENT.resultsH2}
        </Display>
        <SearchResultsLayout
          language={language}
          countryConfig={countryConfig}
          mapData={getSearchMapData(searchResults)}
          mapSettings={mapSettings}
          priceTransparencyLevel={
            (searchResults?.derivedData?.priceTransparencyLevel as PriceTransparencyLevel) ??
            PriceTransparencyLevels.none
          }
          searchErrorMessage={searchErrorMessage}
          preferCatReviews={!!searchResults.derivedData?.shouldPreferCatReviews}
          searchResults={searchResultsWithSelectedSitterUrl}
          isSeoPage
          onClick={() =>
            emitAnalyticsEvent(
              new ClickSitterCard({
                serviceTypeSlug,
                cardType: SitterCardType.DEFAULT,
              })
            )
          }
        />
        <Flex mt="6x" mb={['16x', '12x']} px={['4x', '0x']} width="100%" justifyContent="center">
          <Button
            variant="primary"
            fullWidth
            maxWidth={CTA_MAX_WIDTH}
            rel="nofollow"
            target={isDesktop ? '_blank' : '_self'}
            href={getSearchUrl(searchFilters, language)}
            onClick={() =>
              storeCrossPageAnalyticsEvent(
                new SearchResultsCtaClick({
                  serviceTypeSlug,
                  layoutType: SearchResultLayoutType.DEFAULT,
                })
              )
            }
          >
            <Trans>See more</Trans>
          </Button>
        </Flex>
      </Flex>
    </Section>
  );
};

export default ServiceLandingResultsLayout;
